
import { useEffect, useState } from 'react';
import { Trophy, Calendar, ExternalLink, Copy, Users, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { toast } from 'sonner';

interface BroUser {
  id: string;
  telegram_id: string;
  brocode: string;
  rank: string;
  bro_score: number;
  is_brometheus: boolean;
  twitter_handle?: string;
}

interface EngagementProps {
  user: BroUser;
}

const MiniAppEngagement = ({ user }: EngagementProps) => {
  const [dailyMission, setDailyMission] = useState<any>(null);
  const [tweetTemplate, setTweetTemplate] = useState<string>('');
  const [recentTweets, setRecentTweets] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [submissionUrl, setSubmissionUrl] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { webApp } = useTelegramWebApp();

  // Get authentication headers for API calls
  const getAuthHeaders = () => {
    const headers: { [key: string]: string } = {};
    if (webApp?.initData) {
      headers['x-telegram-data'] = webApp.initData;
    }
    return headers;
  };

  useEffect(() => {
    loadEngagementData();
  }, [user.id]);

  const loadEngagementData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load today's mission
      const today = new Date().toISOString().split('T')[0];
      const { data: mission } = await supabase
        .from('engage_missions')
        .select('*')
        .eq('mission_date', today || '')
        .single();

      if (mission) {
        setDailyMission(mission);
        if (mission.mission_template) {
          generateTweetTemplate(mission.mission_template);
        }
      }

      // Load user's recent tweets
      const { data: tweets } = await supabase
        .from('engagement_tweets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (tweets) {
        setRecentTweets(tweets);
      }

      // Load engagement leaderboard
      const { data: leaders } = await supabase
        .from('initiated_bros')
        .select('brocode, bro_score, rank')
        .order('bro_score', { ascending: false })
        .limit(10);

      if (leaders) {
        setLeaderboard(leaders);
      }
    } catch (error) {
      console.error('Error loading engagement data:', error);
      setError('Failed to load engagement data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateTweetTemplate = (template: string) => {
    // Replace both {BROCODE} and [BROCODE] placeholders with actual BroCode
    let tweetContent = template.replace(/\{BROCODE\}/g, user.brocode);
    tweetContent = tweetContent.replace(/\[BROCODE\]/g, user.brocode);
    setTweetTemplate(tweetContent);
  };

  const copyTweetTemplate = async () => {
    try {
      await navigator.clipboard.writeText(tweetTemplate);
      if (webApp?.HapticFeedback) {
        webApp.HapticFeedback.notificationOccurred('success');
      }
      toast.success('Tweet template copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy tweet template');
    }
  };

  const openTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetTemplate)}`;
    window.open(twitterUrl, '_blank');
  };

  const submitTweet = async () => {
    if (!submissionUrl.trim()) {
      toast.error('Please enter a tweet URL');
      return;
    }

    // Check if user has Twitter handle linked
    if (!user.twitter_handle) {
      toast.error('Please link your Twitter handle first using the Telegram bot command /linktwitter');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Submitting tweet via Supabase function...');
      
      const { data, error } = await supabase.functions.invoke('twitter-api', {
        body: {
          action: 'submit_tweet',
          userId: user.id,
          tweetUrl: submissionUrl,
          isManual: true
        },
        headers: getAuthHeaders()
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to submit tweet');
      }

      if (data?.success) {
        if (webApp?.HapticFeedback) {
          webApp.HapticFeedback.notificationOccurred('success');
        }
        toast.success(`Tweet submitted successfully! +${data.score} BroScore awarded`);
        setSubmissionUrl('');
        loadEngagementData(); // Refresh data
      } else {
        throw new Error(data?.error || 'Failed to submit tweet');
      }
    } catch (error) {
      console.error('Error submitting tweet:', error);
      toast.error(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fixTweetScores = async () => {
    try {
      toast.info('Starting tweet score backfill...');
      
      const { data, error } = await supabase.functions.invoke('twitter-api', {
        body: { action: 'backfill_scores' },
        headers: getAuthHeaders()
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.success) {
        toast.success(data.message || 'Tweet scores updated successfully!');
        loadEngagementData(); // Refresh data
      } else {
        throw new Error(data?.error || 'Failed to fix tweet scores');
      }
    } catch (error) {
      console.error('Error fixing tweet scores:', error);
      toast.error(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-64">
        <div className="flex items-center space-x-2 text-foreground">
          <Loader2 className="animate-spin" size={20} />
          <span>Loading engagement data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-900 border border-red-700 rounded-lg p-4 text-center">
          <AlertCircle className="mx-auto mb-2 text-red-400" size={24} />
          <p className="text-red-200">{error}</p>
          <button
            onClick={loadEngagementData}
            className="mt-3 bg-red-700 text-white px-4 py-2 rounded text-sm hover:bg-red-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show message if no Twitter handle is linked
  if (!user.twitter_handle) {
    return (
      <div className="p-4">
        <div className="bg-secondary rounded-lg p-6 border border-border text-center">
          <h2 className="text-foreground font-bold text-lg mb-4">Link Your Twitter Account</h2>
          <p className="text-muted-foreground mb-4">
            To participate in engagement missions, you need to link your Twitter account first.
          </p>
          <p className="text-foreground text-sm">
            Use the Telegram bot command: <code className="bg-background px-2 py-1 rounded">/linktwitter @yourusername</code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* System Status */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-400" size={16} />
            <span className="text-foreground text-sm font-medium">System Status: Active</span>
          </div>
          <button
            onClick={fixTweetScores}
            className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded hover:bg-primary/90"
          >
            Fix Scores
          </button>
        </div>
      </div>

      {/* Daily Mission */}
      {dailyMission && (
        <div className="bg-secondary rounded-lg p-4 border border-border">
          <h2 className="text-foreground font-bold text-lg mb-2 flex items-center">
            <Trophy className="mr-2" size={20} />
            {dailyMission.mission_title}
          </h2>
          <p className="text-muted-foreground text-sm mb-4">{dailyMission.mission_description}</p>
          
          {/* Tweet Template */}
          <div className="bg-background rounded-lg p-4 mb-4">
            <div className="text-foreground text-sm mb-2">Your Tweet Template:</div>
            <div className="text-muted-foreground text-sm mb-3 italic">"{tweetTemplate}"</div>
            
            <div className="flex gap-2">
              <button
                onClick={copyTweetTemplate}
                className="flex-1 bg-primary text-primary-foreground px-3 py-2 rounded font-bold text-sm flex items-center justify-center hover:bg-primary/90"
              >
                <Copy className="mr-1" size={16} />
                Copy
              </button>
              <button
                onClick={openTwitter}
                className="flex-1 bg-blue-500 text-white px-3 py-2 rounded font-bold text-sm flex items-center justify-center hover:bg-blue-600"
              >
                <ExternalLink className="mr-1" size={16} />
                Tweet
              </button>
            </div>
          </div>

          {/* Tweet Submission */}
          <div className="space-y-3">
            <div className="text-foreground text-sm font-medium">Submit your tweet URL:</div>
            <input
              type="url"
              value={submissionUrl}
              onChange={(e) => setSubmissionUrl(e.target.value)}
              placeholder="https://twitter.com/username/status/..."
              className="w-full bg-background border border-border rounded px-3 py-2 text-foreground text-sm focus:border-primary focus:outline-none"
            />
            <button
              onClick={submitTweet}
              disabled={isSubmitting}
              className="w-full bg-green-600 text-white px-4 py-2 rounded font-bold text-sm disabled:bg-gray-600 hover:bg-green-700 flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Submitting...
                </>
              ) : (
                `Submit Tweet (+${dailyMission.base_points} BroScore)`
              )}
            </button>
          </div>
        </div>
      )}

      {/* Recent Tweets */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <h3 className="text-foreground font-bold mb-3 flex items-center">
          <Calendar className="mr-2" size={18} />
          Recent Submissions
        </h3>
        <div className="space-y-2">
          {recentTweets.map((tweet, index) => (
            <div key={index} className="flex justify-between items-center py-2 border-b border-border border-opacity-30 last:border-b-0">
              <div>
                <div className="text-foreground text-xs">
                  {new Date(tweet.created_at).toLocaleDateString()}
                </div>
                <div className="text-muted-foreground text-xs">
                  {tweet.likes_count || 0} likes • {tweet.comments_count || 0} comments
                </div>
              </div>
              <div className="text-foreground font-bold">+{tweet.total_score || tweet.initial_score || 0}</div>
            </div>
          ))}
          {recentTweets.length === 0 && (
            <div className="text-muted-foreground text-sm text-center py-4">
              No tweets submitted yet. Start with today's mission!
            </div>
          )}
        </div>
      </div>

      {/* Engagement Leaderboard */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <h3 className="text-foreground font-bold mb-3 flex items-center">
          <Users className="mr-2" size={18} />
          Shill Leaderboard
        </h3>
        <div className="space-y-2">
          {leaderboard.map((leader, index) => (
            <div key={index} className="flex justify-between items-center py-2">
              <div className="flex items-center">
                <div className="text-muted-foreground text-sm mr-3 w-6">#{index + 1}</div>
                <div>
                  <div className="text-foreground text-sm font-medium">{leader.brocode}</div>
                  <div className="text-muted-foreground text-xs">{leader.rank}</div>
                </div>
              </div>
              <div className="text-foreground font-bold">{leader.bro_score}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MiniAppEngagement;
